'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Calculator, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { usePermissions } from '@/hooks/use-permissions';
import { useUpdateAppSettingsMutation } from '../services/configurations.mutations';
import { CONFIGURATIONS_HASH } from '../constants';

interface QrSizeCalculatorDialogProps {
  /** Current label width (mm), used as the initial paper width. */
  initialPaperWidthMm: number;
  /** Current label height (mm), used as the initial paper height. */
  initialPaperHeightMm: number;
}

/**
 * Lays out N rows × M columns of QR labels inside a paper of arbitrary
 * dimensions and shows the resulting per-label size. "Apply" writes the
 * computed width/height into the global label-print settings.
 *
 *   per_label_w = (paper_w − 2·margin − (cols − 1)·gap) / cols
 *   per_label_h = (paper_h − 2·margin − (rows − 1)·gap) / rows
 */
export function QrSizeCalculatorDialog({
  initialPaperWidthMm,
  initialPaperHeightMm
}: QrSizeCalculatorDialogProps) {
  const t = useTranslations('configurations.qrCalculator');
  const updateMutation = useUpdateAppSettingsMutation();
  // Applying the computed size PUTs /configurations, which accepts
  // `SuperAdmin|configurations.edit`.
  const { hasPermission } = usePermissions();
  const canEditConfigurations = hasPermission('configurations.edit');
  const { open, onOpenChange } = useHashDialog(
    CONFIGURATIONS_HASH.qrCalculator
  );

  const [paperWidth, setPaperWidth] = useState<string>(
    String(initialPaperWidthMm || 75)
  );
  const [paperHeight, setPaperHeight] = useState<string>(
    String(initialPaperHeightMm || 100)
  );
  const [cols, setCols] = useState<string>('2');
  const [rows, setRows] = useState<string>('2');
  const [margin, setMargin] = useState<string>('0');
  const [gap, setGap] = useState<string>('1');

  const result = useMemo(() => {
    const pw = parseFloat(paperWidth);
    const ph = parseFloat(paperHeight);
    const c = Math.max(1, Math.floor(parseFloat(cols) || 0));
    const r = Math.max(1, Math.floor(parseFloat(rows) || 0));
    const m = Math.max(0, parseFloat(margin) || 0);
    const g = Math.max(0, parseFloat(gap) || 0);
    if (!Number.isFinite(pw) || !Number.isFinite(ph) || pw <= 0 || ph <= 0) {
      return { ok: false as const };
    }
    const labelW = (pw - 2 * m - (c - 1) * g) / c;
    const labelH = (ph - 2 * m - (r - 1) * g) / r;
    return {
      ok: true as const,
      labelWidthMm: labelW,
      labelHeightMm: labelH,
      totalLabels: c * r,
      validDimensions: labelW > 0 && labelH > 0,
      cols: c,
      rows: r
    };
  }, [paperWidth, paperHeight, cols, rows, margin, gap]);

  function handleApply() {
    if (!result.ok || !result.validDimensions) return;
    const pw = parseFloat(paperWidth);
    const ph = parseFloat(paperHeight);
    const m = Math.max(0, parseFloat(margin) || 0);
    const g = Math.max(0, parseFloat(gap) || 0);
    updateMutation.mutate(
      {
        settings: {
          // Computed per-cell label dimensions used by Sticker mode and as
          // the cell size in Grid mode.
          label_width_mm: round3(result.labelWidthMm),
          label_height_mm: round3(result.labelHeightMm),
          label_gap_mm: round3(g),
          // Grid layout inputs reused by Grid mode in the indexing print flow.
          paper_width_mm: round3(pw),
          paper_height_mm: round3(ph),
          grid_cols: result.cols,
          grid_rows: result.rows,
          grid_margin_mm: round3(m)
        }
      },
      { onSuccess: () => onOpenChange(false) }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type='button' size='sm' variant='outline'>
          <Calculator className='me-2 h-4 w-4' />
          {t('open')}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <div className='grid grid-cols-2 gap-3 py-2'>
          <NumberField
            id='qr-calc-paper-w'
            label={t('paperWidth')}
            value={paperWidth}
            onChange={setPaperWidth}
            min={0.1}
            step={0.1}
          />
          <NumberField
            id='qr-calc-paper-h'
            label={t('paperHeight')}
            value={paperHeight}
            onChange={setPaperHeight}
            min={0.1}
            step={0.1}
          />
          <NumberField
            id='qr-calc-cols'
            label={t('columns')}
            value={cols}
            onChange={setCols}
            min={1}
            step={1}
          />
          <NumberField
            id='qr-calc-rows'
            label={t('rows')}
            value={rows}
            onChange={setRows}
            min={1}
            step={1}
          />
          <NumberField
            id='qr-calc-margin'
            label={t('margin')}
            value={margin}
            onChange={setMargin}
            min={0}
            step={0.1}
          />
          <NumberField
            id='qr-calc-gap'
            label={t('gap')}
            value={gap}
            onChange={setGap}
            min={0}
            step={0.1}
          />
        </div>

        <div className='rounded-md border bg-slate-50 p-3 text-sm dark:bg-slate-900/40'>
          {result.ok && result.validDimensions ? (
            <>
              <div className='text-muted-foreground text-xs'>
                {t('resultFor', {
                  cols: result.cols,
                  rows: result.rows,
                  total: result.totalLabels
                })}
              </div>
              <div className='mt-1 grid grid-cols-2 gap-3'>
                <ResultRow
                  label={t('labelWidth')}
                  value={`${round3(result.labelWidthMm)} mm`}
                />
                <ResultRow
                  label={t('labelHeight')}
                  value={`${round3(result.labelHeightMm)} mm`}
                />
              </div>
            </>
          ) : (
            <p className='text-destructive text-xs'>{t('invalidDimensions')}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            type='button'
            onClick={handleApply}
            disabled={
              !result.ok ||
              !result.validDimensions ||
              updateMutation.isPending ||
              !canEditConfigurations
            }
          >
            <Check className='me-2 h-4 w-4' />
            {t('apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min: number;
  step: number;
}

function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  step
}: NumberFieldProps) {
  return (
    <div className='space-y-1.5'>
      <Label htmlFor={id} className='text-sm font-medium'>
        {label}
      </Label>
      <Input
        id={id}
        type='number'
        min={min}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className='h-9 text-sm'
      />
    </div>
  );
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-baseline justify-between gap-2'>
      <span className='text-muted-foreground text-xs'>{label}</span>
      <span className='font-mono text-sm font-semibold'>{value}</span>
    </div>
  );
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
